import { validatePhone } from "./utilities";
import { useCallback, useEffect, useRef, useState } from "react";

export type FieldErrorMap<T extends Record<string, string>> = Partial<Record<keyof T, string>>;
export type FieldValidator = (value: string) => string | null;
export type FieldValidators<T extends Record<string, string>> = Partial<Record<keyof T, FieldValidator[]>>;

export function trimFormValues<T extends Record<string, string>>(values: T): T {
    const trimmedEntries = Object.entries(values).map(([key, value]) => [key, value.trim()]);
    return Object.fromEntries(trimmedEntries) as T;
}

export function requiredFieldValidator(message: string): FieldValidator {
    return (value: string) => (value.trim() === "" ? message : null);
}

export function phoneFieldValidator(message: string): FieldValidator {
    return (value: string) => {
        if (value.trim() === "") return null;
        return validatePhone(value) ? null : message;
    };
}

export function validateFormFields<T extends Record<string, string>>(
    values: T,
    validators: FieldValidators<T>
): FieldErrorMap<T> {
    const errors: FieldErrorMap<T> = {};

    (Object.keys(validators) as Array<keyof T>).forEach((fieldName) => {
        const fieldValidators = validators[fieldName] ?? [];
        const fieldValue = values[fieldName] ?? "";

        for (const validator of fieldValidators) {
            const errorMessage = validator(fieldValue);
            if (errorMessage) {
                errors[fieldName] = errorMessage;
                break;
            }
        }
    });

    return errors;
}

type PendingAction = (() => void) | null;

export function useUnsavedChangesModalGuard(hasUnsavedChanges: boolean) {
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const pendingActionRef = useRef<PendingAction>(null);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasUnsavedChanges) return;

            event.preventDefault();
            (event as any).returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    const requestNavigation = useCallback(
        (action: () => void) => {
            if (!hasUnsavedChanges) {
                action();
                return;
            }

            pendingActionRef.current = action;
            setIsPromptOpen(true);
        },
        [hasUnsavedChanges]
    );

    const confirmNavigation = useCallback(() => {
        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        setIsPromptOpen(false);

        if (action) {
            action();
        }
    }, []);

    const cancelNavigation = useCallback(() => {
        pendingActionRef.current = null;
        setIsPromptOpen(false);
    }, []);

    return {
        isPromptOpen,
        requestNavigation,
        confirmNavigation,
        cancelNavigation,
    };
}